import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { API_BASE } from "../../api/http";
import { electronicInvoicingApi } from "../../api/electronicInvoicingApi";

type ReceivedInvoiceRow = {
  id: string;
  provider?: string | null;
  externalId?: string | null;
  documentType?: string | null;
  documentNumber?: string | null;
  documentDate?: string | null;
  supplierName?: string | null;
  supplierVat?: string | null;
  supplierFiscalCode?: string | null;
  total?: number | string | null;
  currency?: string | null;
  status?: string | null;
  xmlFileName?: string | null;
  xmlContent?: string | null;
  importedAt?: string | null;
  updatedAt?: string | null;
};

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("it-IT", { dateStyle: "short" }).format(date);
}

function formatAmount(value: unknown, currency = "EUR") {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return new Intl.NumberFormat("it-IT", { style: "currency", currency }).format(num);
}

function getStatusBadgeClass(status?: string | null) {
  const s = String(status || "").toUpperCase();
  if (s.includes("DELIVER")) return "text-bg-success";
  if (s.includes("RECEIVED")) return "text-bg-success";
  if (s.includes("PENDING")) return "text-bg-warning";
  if (s.includes("ERROR")) return "text-bg-danger";
  if (s.includes("REJECT")) return "text-bg-danger";
  return "text-bg-secondary";
}

export default function ReceivedInvoicesListPage() {
  const { token } = useAuth() as any;
  const [items, setItems] = useState<ReceivedInvoiceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [alert, setAlert] = useState<{
    type: "success" | "danger" | "warning" | "info";
    text: string;
  } | null>(null);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      setAlert(null);
      const data = await electronicInvoicingApi.listPassiveReceivedInvoices(token);
      setItems(Array.isArray(data) ? data : []);
    } catch (error: any) {
      setAlert({
        type: "danger",
        text: error?.message || "Errore nel caricamento fatture ricevute",
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  async function downloadXml(item: ReceivedInvoiceRow) {
    if (!item?.id || !item?.xmlFileName) return;
    try {
      const res = await fetch(`${API_BASE}/received-invoices/${encodeURIComponent(item.id)}/download-xml`, {
        method: "GET",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.xmlFileName || `${item.id}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setAlert({ type: "danger", text: "Errore durante download XML" });
    }
  }

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      const matchesQuery =
        !q ||
        item.documentNumber?.toLowerCase().includes(q) ||
        item.supplierName?.toLowerCase().includes(q) ||
        item.supplierVat?.toLowerCase().includes(q) ||
        item.supplierFiscalCode?.toLowerCase().includes(q) ||
        item.externalId?.toLowerCase().includes(q);

      const matchesStatus = statusFilter === "ALL" || (item.status || "") === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [items, query, statusFilter]);

  const stats = useMemo(
    () => ({
      total: items.length,
      withXml: items.filter((x) => !!x.xmlFileName).length,
      received: items.filter((x) => String(x.status || "").toUpperCase().includes("RECEIVED")).length,
    }),
    [items],
  );

  const distinctStatuses = useMemo(
    () => Array.from(new Set(items.map((x) => x.status).filter(Boolean))) as string[],
    [items],
  );

  return (
    <div className="container py-4">
      <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4">
        <div>
          <h1 className="h3 mb-1">Fatture ricevute passive</h1>
          <p className="text-muted mb-0">Elenco documenti ricevuti dai fornitori con dettaglio e download XML.</p>
          <p className="small text-muted mb-0">Origine dati: Database locale NAT (`/api/received-invoices`).</p>
        </div>
        <button type="button" className="btn btn-outline-primary" onClick={loadItems} disabled={loading}>
          {loading ? "Aggiornamento..." : "Aggiorna elenco"}
        </button>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type}`} role="alert">
          {alert.text}
        </div>
      )}

      <div className="row g-3 mb-4">
        <div className="col-12 col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="small text-muted mb-1">Totale documenti</div>
              <div className="fs-4 fw-semibold">{stats.total}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="small text-muted mb-1">Ricevuti</div>
              <div className="fs-4 fw-semibold">{stats.received}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="small text-muted mb-1">Con XML disponibile</div>
              <div className="fs-4 fw-semibold">{stats.withXml}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-lg-8">
              <label className="form-label">Cerca documento</label>
              <input
                type="text"
                className="form-control"
                placeholder="Numero documento, fornitore, P.IVA, CF, ID esterno..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="col-12 col-lg-4">
              <label className="form-label">Stato</label>
              <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="ALL">Tutti</option>
                {distinctStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th>Documento</th>
                  <th>Fornitore</th>
                  <th>Data</th>
                  <th>Totale</th>
                  <th>Stato</th>
                  <th>XML</th>
                  <th className="text-end">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      Nessun documento ricevuto trovato.
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="fw-semibold">
                          <Link to={`/received-invoices/${item.id}`} className="text-decoration-none">
                            {item.documentNumber || "Documento senza numero"}
                          </Link>
                        </div>
                        <div className="small text-muted">{item.documentType || "—"}</div>
                      </td>
                      <td>
                        <div>{item.supplierName || "—"}</div>
                        <div className="small text-muted">{item.supplierVat || item.supplierFiscalCode || "—"}</div>
                      </td>
                      <td>{formatDate(item.documentDate)}</td>
                      <td>{formatAmount(item.total, item.currency || "EUR")}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(item.status)}`}>{item.status || "—"}</span>
                      </td>
                      <td>
                        {item.xmlFileName ? (
                          <span className="badge text-bg-success">Disponibile</span>
                        ) : (
                          <span className="badge text-bg-secondary">Assente</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex justify-content-end gap-2">
                          <Link to={`/received-invoices/${item.id}`} className="btn btn-sm btn-outline-primary">
                            Dettaglio
                          </Link>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => downloadXml(item)}
                            disabled={!item.xmlFileName}
                          >
                            Download XML
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
